import { useCallback, useEffect, useState } from 'react';

import { DocumentContent, useDocumentManagerCapability } from '@embedpdf/plugin-document-manager/react';
import { RenderLayer } from '@embedpdf/plugin-render/react';
import { Scroller } from '@embedpdf/plugin-scroll/react';
import { SelectionLayer, useSelectionPlugin } from '@embedpdf/plugin-selection/react';
import { Viewport } from '@embedpdf/plugin-viewport/react';

import PDFSearchBar from './PDFSearchBar';

export default function PDFContent({ documentId, engine }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [resultIndex, setResultIndex] = useState(0);
  const [searching, setSearching] = useState(false);

  const { provides: docManager } = useDocumentManagerCapability();
  const { plugin: selectionPlugin } = useSelectionPlugin();

  const pushHighlights = useCallback(
    (found) => {
      if (!selectionPlugin) return;
      const grouped = {};
      found.forEach((result) => {
        if (!grouped[result.pageIndex]) grouped[result.pageIndex] = [];
        grouped[result.pageIndex].push(...result.rects);
      });
      selectionPlugin.pluginStore.dispatch({ type: 'SELECTION/SET_RECTS', payload: { documentId, rects: grouped } });
      selectionPlugin.notifyAllPages(documentId);
    },
    [selectionPlugin, documentId],
  );

  useEffect(() => {
    if (!query.trim() && selectionPlugin) {
      selectionPlugin.pluginStore.dispatch({ type: 'SELECTION/SET_RECTS', payload: { documentId, rects: {} } });
      selectionPlugin.notifyAllPages(documentId);
      setResults([]);
      setResultIndex(0);
    }
  }, [query, selectionPlugin, documentId]);

  const handleSearch = useCallback(
    async (searchQuery) => {
      if (!searchQuery.trim() || !engine || !docManager) return;
      const doc = docManager.getActiveDocument();
      if (!doc) return;
      setSearching(true);
      try {
        const { results: found } = await engine.searchAllPages(doc, searchQuery.trim()).toPromise();
        setResults(found);
        setResultIndex(0);
        pushHighlights(found);
      } finally {
        setSearching(false);
      }
    },
    [engine, docManager, pushHighlights],
  );

  const handleNavigate = useCallback(
    (direction) => {
      if (!results.length) return;
      setResultIndex((prev) => (prev + direction + results.length) % results.length);
    },
    [results],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PDFSearchBar
        documentId={documentId}
        query={query}
        onQueryChange={setQuery}
        results={results}
        resultIndex={resultIndex}
        searching={searching}
        onSearch={handleSearch}
        onNavigate={handleNavigate}
      />

      <DocumentContent documentId={documentId}>
        {({ isLoading, isError, isLoaded }) => {
          if (isLoading) {
            return (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-text)',
                  opacity: 0.6,
                }}
              >
                Loading document…
              </div>
            );
          }

          if (isError) {
            return (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#dc3545',
                }}
              >
                Failed to load document.
              </div>
            );
          }

          if (!isLoaded) return null;

          return (
            <Viewport
              documentId={documentId}
              style={{
                flex: 1,
                overflow: 'auto',
                backgroundColor: 'var(--color-bg)',
              }}
            >
              <Scroller
                documentId={documentId}
                renderPage={({ width, height, pageIndex }) => {
                  const doc = docManager?.getActiveDocument();
                  const pdfPageWidth = doc?.pages[pageIndex]?.size?.width;
                  const scale = pdfPageWidth ? width / pdfPageWidth : 1;

                  return (
                    <div
                      style={{
                        position: 'relative',
                        width,
                        height,
                        boxShadow: 'var(--shadow-sm, 0 1px 4px rgba(0,0,0,0.15))',
                      }}
                    >
                      <RenderLayer documentId={documentId} pageIndex={pageIndex} />
                      <SelectionLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                        scale={scale}
                        textStyle={{ background: 'rgba(255, 220, 0, 0.5)' }}
                      />
                    </div>
                  );
                }}
              />
            </Viewport>
          );
        }}
      </DocumentContent>
    </div>
  );
}
